import $ from 'jquery';
import {splitCssValue} from '@packages/systems/core/utils/CssStringUtils';
import {emptyObject} from '@packages/utilities/fp/utils';
import {parseIfUnit, type ParsedValue} from './css';
import {
  CSS_PROPERTY_DETAILS,
  type WebflowCssPropertyFull,
} from '@packages/systems/style/properties';
import {
  emptyVariableMap,
  renderCssVariableName,
  VariableCollectionMap,
  VariableMap,
} from '@packages/systems/core/utils/StyleVariables';
import {stringifyCollectionIdToPropertyName} from '@packages/systems/core/utils/StyleVariableModesCssUtils';
/**
 * COPIED FROM `@packages/systems/wfdl`/Plugins.PluginsType['slugMap']
 *
 * Mapping of plugin ID to slug.
 *
 * Typically a Plugin that has a slug was installed from a Library with a
 * matching ID.
 */
type PluginSlugMap = Readonly<{
  [pluginId: string]: string;
}>;

// This type alias is just for clarity about what we expect:
type JQueryNode = any;

function _unwrap(nativeNode: JQueryNode): undefined | null | Node {
  // @ts-expect-error - TS2359 - The right-hand side of an 'instanceof' expression must be of type 'any' or of a type assignable to the 'Function' interface type.
  return nativeNode instanceof $ ? nativeNode.get(0) : nativeNode;
}

function getDocument(nativeNode?: null | Node | JQueryNode) {
  nativeNode = _unwrap(nativeNode);

  if (!nativeNode) {
    return null;
  }

  return nativeNode.ownerDocument;
}

function getWindow(nativeNode?: null | Node | JQueryNode) {
  const doc = getDocument(_unwrap(nativeNode));

  if (!doc) {
    return null;
  }

  return doc.defaultView;
}

// @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type 'WebflowCssPropertyFull[]'.
const cssProperties: WebflowCssPropertyFull[] =
  Object.keys(CSS_PROPERTY_DETAILS);

/**
 * Gets the computed styles for a given element.
 * @param  {Node}     element  The DOM element to get CSS styles for.
 * @param  {String[]} limitTo  An optional array of styles to return in the map.
 * @param  {VariableCollectionMap} variableCollections  The variable collections to return in the map.
 * @param  {VariableMap} variables  An optional map of variables to return in the map.
 * @param  {PluginSlugMap} slugMap  An optional map of plugin slugs to return in the map.
 * @return {Object}             A map of all applied styles. If limitTo is specified
 */
export function getComputedStyles(
  element: Node | JQuery<HTMLElement>,
  variableCollections: VariableCollectionMap,
  limitTo?: string[],
  // NOTE: These are _temporarily_ optional; they are only needed for variable
  // overrides. We will make these required (and likely reorder the arguments)
  // in a separate standalone PR:
  variables: VariableMap = emptyVariableMap,
  slugMap: PluginSlugMap = emptyObject
): {
  [key: string]: string;
} {
  if (!element) {
    return emptyObject;
  }

  if (element instanceof $) {
    // @ts-expect-error - TS2339 - Property 'get' does not exist on type 'Node'.
    element = element.get(0);
  }

  // Autodetect window context (which can be different from the master window if the element is inside an iframe)
  const win = getWindow(element);

  // API: https://developer.mozilla.org/en-US/docs/Web/API/window.getComputedStyle
  const styles = win.getComputedStyle(element, null);
  const map: Record<string, any> = {};

  if (limitTo && limitTo.length) {
    limitTo.forEach((propName) => {
      map[propName] = styles[propName];
    });
  } else {
    for (const variable of variables) {
      const propName = `--${renderCssVariableName(variables, variableCollections, slugMap, variable.id)}`;
      map[propName] = styles.getPropertyValue(propName);
    }

    for (const collectionId of variableCollections.keySeq()) {
      const propName = stringifyCollectionIdToPropertyName(collectionId);
      map[propName] = styles.getPropertyValue(propName);
    }

    for (const propName of cssProperties) {
      map[propName] = styles.getPropertyValue(propName);
    }
  }

  // TEMP: CSS grid initially defined these properties, but are moving to "column-gap"
  // and "row-gap". Chrome does not include these in the enum so we need to manually attach
  // them to the map here.
  if (!map['grid-row-gap'] && styles.gridRowGap) {
    map['grid-row-gap'] = styles.getPropertyValue('grid-row-gap');
  }
  if (!map['grid-column-gap'] && styles.gridColumnGap) {
    map['grid-column-gap'] = styles.getPropertyValue('grid-column-gap');
  }
  if (!map['grid-area'] && styles.gridArea) {
    map['grid-area'] = styles.getPropertyValue('grid-area');
  }

  // Safari 13.1+ implements `background-position` shorthand via reflection and
  // therefore doesn't appear in the longhand id set when we are looping over
  // style. Add `background-position` by using that reflection.
  if (!map['background-position']) {
    map['background-position'] = styles.getPropertyValue('background-position');
  }

  if (!map['font-variation-settings']) {
    map['font-variation-settings'] = styles.getPropertyValue(
      'font-variation-settings'
    );
  }

  if (!map['-webkit-text-stroke-width']) {
    map['-webkit-text-stroke-width'] = styles.getPropertyValue(
      '-webkit-text-stroke-width'
    );
  }

  if (!map['-webkit-text-stroke-color']) {
    map['-webkit-text-stroke-color'] = styles.getPropertyValue(
      '-webkit-text-stroke-color'
    );
  }

  if (!map['white-space']) {
    map['white-space'] = styles.getPropertyValue('whiteSpace');
  }

  if (!map['aspect-ratio']) {
    map['aspect-ratio'] = styles.getPropertyValue('aspectRatio');
  }

  return map;
}

/**
 * Detects an element's transition duration, in milliseconds.
 * @param  {Element} $element A DOM node.
 * @return {Number}           The duration of a transition, if any. If no transition, returns 0. If multiple duration transitions are passed, it returns the longest one.
 */
export function getTransitionDuration($element: Node) {
  // @ts-expect-error - TS2349 - This expression is not callable.
  $element = $($element);

  const rawDurationValues = splitCssValue(
    // @ts-expect-error - TS2339 - Property 'css' does not exist on type 'Node'.
    $element.css('transition-duration'),
    ','
  );
  let maxDuration = 0;
  for (let i = 0; i < rawDurationValues.length; i++) {
    const parsedDuration: ParsedValue = parseIfUnit(
      rawDurationValues[i]
    ) as any;
    if (!parsedDuration) {
      continue;
    }
    const {unit, value} = parsedDuration;
    if (unit === 'ms' || unit === 's') {
      maxDuration = Math.max(value * (unit === 's' ? 1000 : 1), maxDuration);
    }
  }
  return maxDuration;
}
